import {
  Button,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Text,
  Thead,
  Tr
} from '@chakra-ui/react';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { NoticeMessageType } from '../../../pages/NoticeMessageList';
import { SetMessageData } from '../../../redux/message/MessageSlice';
import { DivisionEnum } from '../Message/MessageBody';

interface props {
  onOpen: () => void;
  onConfirm: () => void;
  setChooseRow: React.Dispatch<React.SetStateAction<number | undefined>>;
  GetAlertName: (level: number) => string;
  MessageData: NoticeMessageType[] | undefined;
  PageDispMax: number;
  PageDispNow: number;
}

const MaxCharacterOneLine = 40;

export const MessageInfoExists = (
  messageData: NoticeMessageType,
  type: number
) => {
  switch (type) {
    case DivisionEnum.Tram:
      if (messageData.messageSignagesData !== null) {
        return true;
      }
      break;
    case DivisionEnum.Train:
      if (messageData.messageTrainsData !== null) {
        return true;
      }
      break;
    case DivisionEnum.Location:
      if (messageData.messageLocationData !== null) {
        return true;
      }
      break;
  }
  return false;
};

const DateToString = (date: Date) => {
  if (date === undefined) return '';
  const datetime = new Date(date);
  return (
    datetime.getFullYear().toString() +
    '/' +
    (datetime.getMonth().toString().length === 1 ? '0' : '') +
    (datetime.getMonth() + 1).toString() +
    '/' +
    (datetime.getDate().toString().length === 1 ? '0' : '') +
    datetime.getDate().toString() +
    ' ' +
    (datetime.getHours().toString().length === 1 ? '0' : '') +
    datetime.getHours().toString() +
    ':' +
    (datetime.getMinutes().toString().length === 1 ? '0' : '') +
    datetime.getMinutes().toString() +
    ':' +
    (datetime.getSeconds().toString().length === 1 ? '0' : '') +
    datetime.getSeconds().toString()
  );
};

// メッセージの表示を何行にするか決める配列作成（map私用の為）
const GetLineMaxCount = (message: string) => {
  // 文字数を最大行表示文字で割り、あまりを表示するため一行追加する
  const array = new Array(Math.floor(message.length / MaxCharacterOneLine) + 1);
  // 配列がemptyだとmapできないのでnull代入
  for (let i = 0; i < array.length; i++) {
    array[i] = null;
  }
  return array;
};

// 一行表示文字列取得
const GetMessageOneLine = (message: string, count: number) => {
  let oneLine = '';
  if (message.length > MaxCharacterOneLine * (count + 1)) {
    // 40文字になるよう文字列を作成
    for (
      let charCount = count * MaxCharacterOneLine;
      charCount < MaxCharacterOneLine * (count + 1);
      charCount++
    ) {
      oneLine = oneLine.concat(message[charCount]);
    }
  } else {
    for (
      let charCount = count * MaxCharacterOneLine;
      charCount < message.length;
      charCount++
    ) {
      oneLine = oneLine.concat(message[charCount]);
    }
  }
  return oneLine;
};

export const NoticeMessageListBody: React.FC<props> = (props) => {
  const {
    onOpen,
    onConfirm,
    setChooseRow,
    GetAlertName,
    MessageData,
    PageDispMax,
    PageDispNow
  } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  return (
    <>
      <TableContainer
        position='absolute'
        gridRow='1/2'
        h='100%'
        w='100%'
        mt={5}
        overflowY='auto'
        overflowX='unset'
      >
        <Table variant='simple'>
          <Thead borderWidth='1px' position='sticky' top={0} zIndex='docked'>
            <Tr bg='gray.800'>
              <Th w='2%' textAlign='center' borderWidth='1px' color='white'>
                OBC
              </Th>
              <Th w='2%' textAlign='center' borderWidth='1px' color='white'>
                停留場
              </Th>
              <Th w='2%' textAlign='center' borderWidth='1px' color='white'>
                位置情報
              </Th>
              <Th w='5%' textAlign='center' borderWidth='1px' color='white'>
                警戒レベル
              </Th>
              <Th w='10%' textAlign='center' borderWidth='1px' color='white'>
                カテゴリ
              </Th>
              <Th
                maxW='500px'
                textAlign='center'
                borderWidth='1px'
                color='white'
              >
                メッセージ
              </Th>
              <Th w='10%' textAlign='center' borderWidth='1px' color='white'>
                配信期間(自)
              </Th>
              <Th w='10%' textAlign='center' borderWidth='1px' color='white'>
                配信期間(至)
              </Th>
              <Th w='5%' textAlign='center' borderWidth='1px' color='white'>
                ステータス
              </Th>
              <Th
                w='4%'
                textAlign='center'
                borderWidth='1px'
                color='white'
              ></Th>
            </Tr>
          </Thead>
          <Tbody>
            {MessageData?.map((data, i) => {
              if (
                PageDispMax * PageDispNow - PageDispMax <= i &&
                i < PageDispMax * PageDispNow
              ) {
                return (
                  <Tr
                    key={'MessageRow_' + i}
                    w='50px'
                    h='50px'
                    whiteSpace='unset'
                  >
                    <Td textAlign='center'>
                      {MessageInfoExists(data, DivisionEnum.Train) ? '✔' : ''}
                    </Td>
                    <Td textAlign='center'>
                      {MessageInfoExists(data, DivisionEnum.Tram) ? '✔' : ''}
                    </Td>
                    <Td textAlign='center'>
                      {MessageInfoExists(data, DivisionEnum.Location)
                        ? '✔'
                        : ''}
                    </Td>
                    <Td>{GetAlertName(data.alert_level)}</Td>
                    <Td>{data.category_name}</Td>
                    <Td>
                      {GetLineMaxCount(data.message).map((_, i) => {
                        return (
                          <Text key={'messageLine_' + i}>
                            {GetMessageOneLine(data.message, i)}
                          </Text>
                        );
                      })}
                    </Td>
                    <Td>{DateToString(data.term_from)}</Td>
                    <Td>{DateToString(data.term_to)}</Td>
                    <Td>
                      <Button
                        onClick={() => {
                          setChooseRow(i);
                          onOpen();
                        }}
                      >
                        詳細
                      </Button>
                    </Td>
                    <Td>
                      <HStack>
                        <u>
                          <label
                            style={{
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              dispatch(SetMessageData({ data: data }));
                              navigate('/message');
                            }}
                          >
                            編集
                          </label>
                        </u>
                        <u>
                          <label
                            style={{
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setChooseRow(i);
                              onConfirm();
                            }}
                          >
                            削除
                          </label>
                        </u>
                      </HStack>
                    </Td>
                  </Tr>
                );
              }
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
};
